import getIsJunkValueToSet from 'mini-mail-triage-actions/lib/utils/markAsJunk/getIsJunkValueToSet';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import isItemView from 'owa-mail-store/lib/utils/isItemView';
import React from 'react';
import strings from 'owa-strings/owa-mini-strings';
import toggleMoveToFolderPane from 'mini-mail-folder-store/lib/actions/toggleMoveToFolderPane';
import { getIsAnySelectedUnread } from 'mini-mail-list-store/lib/utils/getTriageState';
import { Header, HeaderIcon } from 'mini-header';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { isIOS } from 'mini-device-utils/lib/utils/isIOS';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { MailIcons } from 'mini-mail-icons';
import { MiniIcons } from 'owa-mini-icons';
import { observer } from 'mobx-react-lite';
import { useRegisterWorkflowActions } from 'mini-mail-workflow-tracking/lib/utils/useRegisterWorkflowActions';
import {
    lazyDeleteSelectedConversations,
    lazyApplySelectedConversationsFlaggedState,
    lazyMarkSelectedConversationsJunkNotJunk,
    lazyDeleteSelectedItems,
    lazyMarkSelectedItemsJunkNotJunk,
    lazyApplySelectedItemsFlaggedState,
    lazyMarkSelectedItemsReadState,
    lazyMarkSelectedConversationsReadState,
} from 'mini-mail-triage-actions';
import {
    getMarkAsReadAriaLabel,
    getMarkAsFlaggedAriaLabel,
    getMarkAsJunkAriaLabel,
    getDeleteAriaLabel,
    getMoveToFolderAriaLabel,
} from '../utils/accessibility/getMultiSelectFooterAriaLabels';
import {
    ACTION_DELETE_SELECTED_ITEMS,
    ACTION_DELETE_SELECTED_CONVERSATIONS,
} from 'mini-mail-triage-actions/lib/actions/constants/ListViewActionNames';
import {
    DELETE_SELECTED_MAIL_ITEMS_BEGIN,
    DELETE_SELECTED_CONVERSATIONS_BEGIN,
} from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';

const styles = require('./MultiSelect.scss');
const classNames = require('classnames/bind').bind(styles);

const onMarkSelectedAsRead = async () =>
    isItemView()
        ? await lazyMarkSelectedItemsReadState()
        : await lazyMarkSelectedConversationsReadState();

const onFlagSelected = () =>
    isItemView()
        ? lazyApplySelectedItemsFlaggedState()
        : lazyApplySelectedConversationsFlaggedState();

const onDeleteSelected = () =>
    isItemView() ? lazyDeleteSelectedItems() : lazyDeleteSelectedConversations();

const onMarkAsJunkSelected = async () =>
    isItemView()
        ? lazyMarkSelectedItemsJunkNotJunk()
        : await lazyMarkSelectedConversationsJunkNotJunk();

interface MultiSelectFooterProps {
    shouldPositionBeFixed?: boolean;
}

const MultiSelectFooter: React.FC<MultiSelectFooterProps> = (props): JSX.Element => {
    const { shouldPositionBeFixed = true } = props ?? {};
    const isInVirtualSelectAllMode = getSelectedTableView().isInVirtualSelectAllMode;

    const leftIcons: HeaderIcon[] = [
        {
            // Read/Unread
            icon: getIsAnySelectedUnread() ? MailIcons.Read : MiniIcons.Mail,
            onClick: onMarkSelectedAsRead,
            ariaLabel: getMarkAsReadAriaLabel(),
            iconStyle: styles.multiSelectFooterIcon,
        },
        !isInVirtualSelectAllMode && {
            // Flag/Unflag
            icon: MailIcons.Flag,
            onClick: onFlagSelected,
            ariaLabel: getMarkAsFlaggedAriaLabel(),
            iconStyle: styles.multiSelectFooterIcon,
        },
        !isInVirtualSelectAllMode && {
            // Move to folder
            icon: MailIcons.FabricMovetoFolder,
            onClick: toggleMoveToFolderPane,
            ariaLabel: getMoveToFolderAriaLabel(),
            iconStyle: styles.multiSelectFooterIcon,
        },
        {
            // Delete
            icon: MiniIcons.Delete,
            onClick: onDeleteSelected,
            ariaLabel: getDeleteAriaLabel(),
            iconStyle: styles.multiSelectFooterIcon,
        },
        isMiniFeatureEnabled('mini-markAsJunkMultiSelect') &&
            getIsJunkValueToSet() &&
            !isInVirtualSelectAllMode && {
                // Mark as Junk
                icon: MiniIcons.Blocked,
                onClick: onMarkAsJunkSelected,
                ariaLabel: getMarkAsJunkAriaLabel(),
                iconStyle: styles.multiSelectFooterIcon,
            },
    ].filter(Boolean);

    useRegisterWorkflowActions({
        [ACTION_DELETE_SELECTED_ITEMS]: DELETE_SELECTED_MAIL_ITEMS_BEGIN,
        [ACTION_DELETE_SELECTED_CONVERSATIONS]: DELETE_SELECTED_CONVERSATIONS_BEGIN,
    });

    return (
        <Header
            headerStyles={classNames(
                styles.multiSelectFooter,
                { [styles.fixedAtBottom]: shouldPositionBeFixed },
                { [styles.iOSSafeArea]: isIOS() },
                {
                    [styles.multiSelectFooterLandscape]: !isInSearchView(),
                }
            )}
            headerAriaLabel={strings.multiSelectActionsFooterAriaLabel}
            leftIcon={leftIcons}
            wrapLeftIconsInContainer={false}
            wrapRightIconsInContainer={false}
            hideExtensionHeader
        />
    );
};

export default observer(MultiSelectFooter);
